import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useHasRole } from "@/components/RoleGate";
import { COMMON_CURRENCIES, formatCurrency, formatPercent } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function ProductDialog({ mode, open, onOpenChange, initial, defaults, onSubmit, submitting }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    description: "",
    sku: "",
    unit: "",
    price: "",
    currency: "SEK",
    tax_rate: 0,
    is_active: true,
  });

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && initial) {
      setForm({
        name: initial.name || "",
        description: initial.description || "",
        sku: initial.sku || "",
        unit: initial.unit || "",
        price: initial.price?.toString() || "0",
        currency: initial.currency || defaults.currency,
        tax_rate: initial.tax_rate ?? 0,
        is_active: !!initial.is_active,
      });
    } else {
      setForm({
        name: "",
        description: "",
        sku: "",
        unit: "",
        price: "",
        currency: defaults.currency,
        tax_rate: 0,
        is_active: true,
      });
    }
  }, [open, mode, initial, defaults.currency]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const payload = {
      name: form.name,
      description: form.description || null,
      sku: form.sku || null,
      unit: form.unit || null,
      price: Number(form.price),
      currency: form.currency,
      tax_rate: Number(form.tax_rate),
    };
    if (mode === "edit") payload.is_active = form.is_active;
    await onSubmit(payload);
  }

  const titleKey = mode === "edit" ? "products.edit.title" : "products.create.title";
  const descKey = mode === "edit" ? "products.edit.description" : "products.create.description";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-xl"
        data-testid={`product-dialog-${mode}`}
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl tracking-tight">
            {t(titleKey)}
          </DialogTitle>
          <DialogDescription>{t(descKey)}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="prod-name">{t("products.form.name")}</Label>
            <Input
              id="prod-name"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="product-form-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="prod-desc">{t("products.form.description")}</Label>
            <Textarea
              id="prod-desc"
              rows={2}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="product-form-description"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="prod-sku">{t("products.form.sku")}</Label>
              <Input
                id="prod-sku"
                value={form.sku}
                onChange={(e) => set("sku", e.target.value)}
                data-testid="product-form-sku"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prod-unit">{t("products.form.unit")}</Label>
              <Input
                id="prod-unit"
                placeholder="hour, pcs, month..."
                value={form.unit}
                onChange={(e) => set("unit", e.target.value)}
                data-testid="product-form-unit"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prod-price">{t("products.form.price")}</Label>
              <Input
                id="prod-price"
                type="number"
                step="0.01"
                min={0}
                required
                value={form.price}
                onChange={(e) => set("price", e.target.value)}
                data-testid="product-form-price"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("products.form.currency")}</Label>
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="product-form-currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="prod-tax">{t("products.form.taxRate")}</Label>
              <Input
                id="prod-tax"
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={form.tax_rate}
                onChange={(e) => set("tax_rate", e.target.value)}
                data-testid="product-form-tax"
              />
            </div>
          </div>

          {mode === "edit" && (
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <Label htmlFor="prod-active" className="cursor-pointer">
                {t("products.form.active")}
              </Label>
              <Switch
                id="prod-active"
                checked={form.is_active}
                onCheckedChange={(v) => set("is_active", v)}
                data-testid="product-form-active"
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="product-form-cancel"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="product-form-submit"
              className="bg-slate-900 text-white hover:bg-slate-800"
            >
              {mode === "edit" ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const { t, i18n } = useTranslation();
  const { company } = useAuth();
  const canManage = useHasRole("admin", "accountant");
  const canDelete = useHasRole("admin");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [dialogState, setDialogState] = useState({ open: false, mode: "create", initial: null });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const defaults = useMemo(
    () => ({ currency: company?.default_currency || "SEK" }),
    [company]
  );

  const language = i18n.language?.split("-")[0] || "en";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (!showInactive) params.set("is_active", "true");
      params.set("page", "1");
      params.set("page_size", "100");
      const { data } = await api.get(`/products?${params.toString()}`);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatApiError(err, t("products.toasts.error")));
    } finally {
      setLoading(false);
    }
  }, [search, showInactive, t]);

  useEffect(() => {
    const handle = setTimeout(load, 250);
    return () => clearTimeout(handle);
  }, [load]);

  function openCreate() {
    setDialogState({ open: true, mode: "create", initial: null });
  }
  function openEdit(p) {
    setDialogState({ open: true, mode: "edit", initial: p });
  }

  async function handleSubmit(payload) {
    setSubmitting(true);
    try {
      if (dialogState.mode === "create") {
        await api.post("/products", payload);
        toast.success(t("products.toasts.created"));
      } else {
        await api.patch(`/products/${dialogState.initial.id}`, payload);
        toast.success(t("products.toasts.updated"));
      }
      setDialogState({ open: false, mode: "create", initial: null });
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("products.toasts.error")));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setSubmitting(true);
    const deletedId = confirmDelete.id;
    try {
      await api.delete(`/products/${deletedId}`);
      toast.success(t("products.toasts.deleted"));
      setConfirmDelete(null);
      setItems((curr) => curr.filter((x) => x.id !== deletedId));
      setTotal((t0) => Math.max(0, t0 - 1));
      load();
    } catch (err) {
      toast.error(formatApiError(err, t("products.toasts.error")));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-8" data-testid="products-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-2">
          <h1 className="font-heading text-4xl tracking-tight text-slate-900">
            {t("products.title")}
          </h1>
          <p className="max-w-xl text-sm leading-relaxed text-slate-500">
            {t("products.subtitle", { company: company?.name || "—" })}
          </p>
        </div>
        {canManage && (
          <Button
            onClick={openCreate}
            data-testid="products-add-button"
            className="gap-2 bg-slate-900 text-white hover:bg-slate-800"
          >
            <Plus className="h-4 w-4" />
            {t("products.addProduct")}
          </Button>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder={t("common.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ps-9"
            data-testid="products-search"
          />
        </div>
        <label className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600">
          <Switch
            checked={showInactive}
            onCheckedChange={setShowInactive}
            data-testid="products-show-inactive"
          />
          <span>{t("common.showInactive")}</span>
        </label>
        <div className="ms-auto text-xs text-slate-500" data-testid="products-total">
          {t("common.totalCount", { count: total })}
        </div>
      </div>

      <div className="mobile-cards overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("products.table.name")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("products.table.sku")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("products.table.unit")}
              </TableHead>
              <TableHead className="text-end text-xs uppercase tracking-wide text-slate-500">
                {t("products.table.price")}
              </TableHead>
              <TableHead className="text-end text-xs uppercase tracking-wide text-slate-500">
                {t("products.table.tax")}
              </TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-slate-500">
                {t("products.table.status")}
              </TableHead>
              <TableHead className="text-end text-xs uppercase tracking-wide text-slate-500">
                {t("common.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-sm text-slate-500">
                  {t("products.empty")}
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              items.map((p) => (
                <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                  <TableCell data-label={t("products.table.name")}>
                    <div className="font-medium text-slate-900">{p.name}</div>
                    {p.description && (
                      <div className="mt-0.5 max-w-md truncate text-xs text-slate-500">
                        {p.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500 font-mono text-xs" data-label={t("products.table.sku")}>
                    {p.sku || "—"}
                  </TableCell>
                  <TableCell className="text-slate-600 text-xs" data-label={t("products.table.unit")}>
                    {p.unit || "—"}
                  </TableCell>
                  <TableCell
                    className="text-end font-medium tabular-nums text-slate-900"
                    data-testid={`product-price-${p.id}`}
                    data-label={t("products.table.price")}
                  >
                    {formatCurrency(p.price, p.currency, language)}
                  </TableCell>
                  <TableCell className="text-end text-slate-600 tabular-nums" data-label={t("products.table.tax")}>
                    {formatPercent(p.tax_rate, language)}
                  </TableCell>
                  <TableCell data-label={t("products.table.status")}>
                    {p.is_active ? (
                      <span className="inline-flex items-center gap-2 text-xs text-emerald-700">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        {t("common.active")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2 text-xs text-slate-500">
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                        {t("common.inactive")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-end" data-label={t("common.actions")}>
                    <div className="inline-flex items-center gap-1">
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(p)}
                          data-testid={`product-edit-${p.id}`}
                          className="gap-1 text-slate-700"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          {t("common.edit")}
                        </Button>
                      )}
                      {canDelete && p.is_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDelete(p)}
                          data-testid={`product-delete-${p.id}`}
                          className="gap-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t("common.delete")}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <ProductDialog
        mode={dialogState.mode}
        open={dialogState.open}
        onOpenChange={(o) =>
          setDialogState((s) => ({ ...s, open: o, initial: o ? s.initial : null }))
        }
        initial={dialogState.initial}
        defaults={defaults}
        onSubmit={handleSubmit}
        submitting={submitting}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
      >
        <AlertDialogContent data-testid="product-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("products.delete.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("products.delete.description", {
                name: confirmDelete?.name || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={submitting}
              data-testid="product-delete-confirm-button"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t("products.delete.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
